import { IConsoleCapture } from '../../domain/interfaces/IConsoleCapture';
import { LogEntry, LogLevel } from '../../domain/types';

export class ConsoleCapture implements IConsoleCapture {
  private originalMethods: { [key: string]: Function } = {};
  private callback: ((entry: LogEntry) => void) | null = null;
  private isActive = false;

  start(callback: (entry: LogEntry) => void): void {
    if (this.isActive) return;
    
    this.callback = callback;
    this.isActive = true;
    
    this.patchConsole();
    this.setupErrorHandlers();
  }

  stop(): void {
    if (!this.isActive) return;
    
    this.restoreConsole();
    this.removeErrorHandlers();
    this.callback = null;
    this.isActive = false;
  }

  captureError(error: Error): LogEntry {
    return {
      level: 'error',
      msg: error.message,
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'console',
        stack: error.stack,
        url: window.location.href
      }
    };
  }

  private patchConsole(): void {
    // Only capture errors and warnings, skip info/debug logs
    const methods: Array<{ name: keyof Console; level: LogLevel }> = [
      { name: 'warn', level: 'warn' },
      { name: 'error', level: 'error' }
    ];

    methods.forEach(({ name, level }) => {
      this.originalMethods[name] = console[name];
      const self = this;
      
      (console as any)[name] = function(...args: any[]) {
        // Call original method
        self.originalMethods[name].apply(console, args);
        
        // Capture log
        if (self.callback) {
          const msg = args.map(arg => {
            if (typeof arg === 'object') {
              try {
                return JSON.stringify(arg);
              } catch {
                return String(arg);
              }
            }
            return String(arg);
          }).join(' ');

          self.callback({
            level,
            msg,
            ts: new Date().toISOString(),
            source: 'browser',
            meta: {
              type: 'console',
              url: window.location.href
            }
          });
        }
      };
    });
  }

  private restoreConsole(): void {
    Object.keys(this.originalMethods).forEach(method => {
      (console as any)[method] = this.originalMethods[method];
    });
    this.originalMethods = {};
  }

  private setupErrorHandlers(): void {
    window.addEventListener('error', this.handleError);
    window.addEventListener('unhandledrejection', this.handleRejection);
  }

  private removeErrorHandlers(): void {
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleRejection);
  }

  private handleError = (event: ErrorEvent): void => {
    if (this.callback) {
      this.callback({
        level: 'error',
        msg: event.message,
        ts: new Date().toISOString(),
        source: 'browser',
        meta: {
          type: 'console',
          stack: event.error?.stack,
          url: event.filename,
          line: event.lineno,
          column: event.colno
        }
      });
    }
  };

  private handleRejection = (event: PromiseRejectionEvent): void => {
    if (this.callback) {
      const msg = event.reason?.message || String(event.reason);
      this.callback({
        level: 'error',
        msg: `Unhandled Promise Rejection: ${msg}`,
        ts: new Date().toISOString(),
        source: 'browser',
        meta: {
          type: 'console',
          stack: event.reason?.stack,
          url: window.location.href
        }
      });
    }
  };
}